import { FormEvent, useState } from 'react';
import { EnquiryErrors, EnquiryValues, sendEnquiry, validateEnquiry } from '../utils/enquiry';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMPTY: EnquiryValues = {
  name: '',
  email: '',
  phone: '',
  course: '',
  location: '',
  message: '',
  consent: false,
};

export function useEnquiryForm(initial: Partial<EnquiryValues> = {}) {
  const [values, setValues] = useState<EnquiryValues>({ ...EMPTY, ...initial });
  const [errors, setErrors] = useState<EnquiryErrors>({});
  const [status, setStatus] = useState<Status>('idle');

  function update<K extends keyof EnquiryValues>(key: K, value: EnquiryValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found = validateEnquiry(values);
    setErrors(found);
    const firstError = Object.keys(found)[0];
    if (firstError) {
      const field = event.currentTarget.querySelector<HTMLElement>(`[name="${firstError}"]`);
      field?.focus();
      return;
    }
    setStatus('submitting');
    try {
      await sendEnquiry(values);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  function reset() {
    setValues({ ...EMPTY, ...initial });
    setErrors({});
    setStatus('idle');
  }

  return { values, errors, status, update, submit, reset };
}
